import React, { useState } from 'react';
import BrandLogo from './BrandLogo';
import { Instagram, Twitter, Facebook, Linkedin, Send } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Link, useNavigate } from 'react-router-dom';

const SOCIALS = [
  { icon: Instagram, label: 'Instagram', href: 'https://www.instagram.com/the_brain_theory?utm_source=qr&igsh=MXh4bjZhNmh5ZW9pNA%3D%3D' },
  { icon: Facebook,  label: 'Facebook',  href: 'https://www.facebook.com/profile.php?id=61590731788615' },
  { icon: Twitter,   label: 'X',         href: 'https://x.com/thebraintheory' },
  { icon: Linkedin,  label: 'LinkedIn',  href: 'https://www.linkedin.com/company/the-brain-theory/about/?viewAsMember=true' },
];

const Footer = () => {
  const [email, setEmail] = useState('');
  const navigate = useNavigate();

  const subscribe = (e) => {
    e.preventDefault();
    if (!email.match(/.+@.+\..+/)) {
      toast({ title: 'Hmm, that email looks off', description: 'Please enter a valid address' });
      return;
    }
    toast({ title: 'You are in!', description: 'We will send you gentle, useful updates' });
    setEmail('');
  };

  const scrollTo = (id) => {
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.querySelector(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 60);
    } else {
      const el = document.querySelector(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-[#3D2817] border-t border-[#4a3221] pt-12 sm:pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-8 sm:gap-10">
          <div className="lg:col-span-2">
            <BrandLogo size="md" />
            <p className="mt-4 sm:mt-5 text-[#B8A990] max-w-sm leading-relaxed text-sm sm:text-base">
              Fun, daily and science-backed brain training designed to welcome every age — from curious kids to lifelong learners
            </p>
            <form onSubmit={subscribe} className="mt-5 sm:mt-6 flex items-center gap-2 max-w-sm">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 rounded-full bg-[#2A1810] border border-[#5a3d24] px-4 sm:px-5 py-2.5 sm:py-3 text-sm text-[#F5EDE0] placeholder:text-[#8f7658] focus:outline-none focus:border-[#F4C430]"
              />
              <button
                type="submit"
                className="h-10 w-10 sm:h-11 sm:w-11 rounded-full bg-[#F4C430] hover:bg-[#FFD34E] text-[#2A1810] grid place-items-center shrink-0"
                aria-label="subscribe"
              >
                <Send size={16} />
              </button>
            </form>
            <div className="mt-5 sm:mt-6 flex gap-2" data-testid="footer-socials">
              {SOCIALS.map((s, i) => (
                <a
                  key={i}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-${s.label.toLowerCase()}`}
                  aria-label={s.label}
                  className="h-10 w-10 rounded-full grid place-items-center border border-[#5a3d24] text-[#D9C9B3] hover:border-[#F4C430] hover:text-[#F4C430] transition-colors"
                >
                  <s.icon size={16} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h5 className="font-serif text-[#F5EDE0] text-lg">Product</h5>
            <ul className="mt-3 sm:mt-4 space-y-2.5">
              <li><button onClick={() => scrollTo('#how-it-works')} className="text-[#B8A990] hover:text-[#F4C430] text-sm">How It Works</button></li>
              <li><button onClick={() => scrollTo('#pricing')} className="text-[#B8A990] hover:text-[#F4C430] text-sm">Pricing</button></li>
              <li><button onClick={() => scrollTo('#categories')} className="text-[#B8A990] hover:text-[#F4C430] text-sm">Categories</button></li>
              <li><button onClick={() => scrollTo('#testimonials')} className="text-[#B8A990] hover:text-[#F4C430] text-sm">Testimonials</button></li>
            </ul>
          </div>

          <div>
            <h5 className="font-serif text-[#F5EDE0] text-lg">Company</h5>
            <ul className="mt-3 sm:mt-4 space-y-2.5">
              <li><Link to="/about" className="text-[#B8A990] hover:text-[#F4C430] text-sm">About Us</Link></li>
              <li><Link to="/help" className="text-[#B8A990] hover:text-[#F4C430] text-sm">Help</Link></li>
              <li><button onClick={() => toast({ title: 'Careers page coming soon', description: 'Want to work with us? Write to thebraintheoryofficial@gmail.com' })} className="text-[#B8A990] hover:text-[#F4C430] text-sm">Careers</button></li>
              <li><button onClick={() => toast({ title: 'Press kit coming soon', description: 'For media enquiries, please email us' })} className="text-[#B8A990] hover:text-[#F4C430] text-sm">Press</button></li>
            </ul>
          </div>

          <div>
            <h5 className="font-serif text-[#F5EDE0] text-lg">Support</h5>
            <ul className="mt-3 sm:mt-4 space-y-2.5">
              <li><Link to="/help#faq"     className="text-[#B8A990] hover:text-[#F4C430] text-sm" data-testid="footer-faq">FAQ</Link></li>
              <li><Link to="/contact" className="text-[#B8A990] hover:text-[#F4C430] text-sm" data-testid="footer-contact">Contact Us</Link></li>
              <li><Link to="/privacy" className="text-[#B8A990] hover:text-[#F4C430] text-sm" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/terms"   className="text-[#B8A990] hover:text-[#F4C430] text-sm" data-testid="footer-terms">Terms</Link></li>
              <li><Link to="/refund-policy" className="text-[#B8A990] hover:text-[#F4C430] text-sm" data-testid="footer-refund">Refund & Cancellation</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-10 sm:mt-14 pt-6 border-t border-[#4a3221] flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-[#8f7658] text-xs sm:text-sm">
            © {new Date().getFullYear()} The Brain Theory — made with warmth
          </p>
          <p className="text-[#8f7658] text-xs sm:text-sm tracking-wider">TRAIN · IMPROVE · EVOLVE</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
